import React, { useMemo } from "react";
import { Box, Typography, Tooltip, Paper } from "@mui/material";

/**
 * Gantt-style ATO timeline replicating the IDEX Main view: a 24-hour Zulu
 * axis with one bar per mission, grouped under platform/INT header rows.
 * The dim bar spans takeoff→landing; the bright core spans onStation→offStation.
 */

export interface TimelineMission {
  _id: string;
  callsign: string;
  takeoff?: string | null;
  landing?: string | null;
  onStation: string | null;
  offStation: string | null;
  armed: boolean;
  locked: boolean;
  hold: boolean;
  holdType?: string;
  canx: boolean;
  /** Most recent change type, for the IDEX-style change badge on the bar */
  lastChangeType?: string | null;
  launchBase?: string;
}

// Abbreviations for the bar badge; Hold/CANX are covered by the flag glyphs.
const CHANGE_BADGES: Record<string, string> = {
  Delay: "DLY",
  Extension: "EXT",
  "Early RTB": "RTB",
  Divert: "DIV",
  Retask: "RT",
  "Lost Link": "LL",
  "Downed Asset": "DA",
  Reaction: "RX",
  "Strike Support": "STK",
  "Cross Cue": "CC",
  "Alt Tasking": "AT",
  "Vul Shift": "VS",
  "Minor Variation": "MV",
  "Hybrid Management": "HM",
  "Launch Ground Alert": "GA",
  "Re-Launched": "RL",
  "New Callsign": "NC",
  "Early On-Station": "EOS",
}

export interface TimelineGroup {
  name: string;
  color: string;
  missions: TimelineMission[];
}

interface AtoTimelineProps {
  groups: TimelineGroup[];
  /** ATO day in YYYY-MM-DD; the axis runs 24h from startHour Z of this day */
  atoDate: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  sortBy: "TAKEOFF" | "CALLSIGN";
  /** Zulu hour the ATO day starts at (IDEX "ATO Day Start Time"), default 0 */
  startHour?: number;
}

const ROW_HEIGHT = 26;
const HEADER_HEIGHT = 22;
const AXIS_HEIGHT = 24;
const LABEL_WIDTH = 0; // callsigns render on the bars, like IDEX

function parseTime(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/** Total on-station time across a group's missions, as "HH:MM" (IDEX shows this per lane group). */
function collectionTotal(missions: TimelineMission[]): string {
  let ms = 0;
  for (const m of missions) {
    const on = parseTime(m.onStation);
    const off = parseTime(m.offStation);
    if (on !== null && off !== null && off > on) ms += off - on;
  }
  const mins = Math.round(ms / 60000);
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
}

/** Position of a timestamp within the ATO day as a 0–100 percentage, clamped. */
function pct(t: number, dayStart: number, dayEnd: number): number {
  return Math.min(100, Math.max(0, ((t - dayStart) / (dayEnd - dayStart)) * 100));
}

export default function AtoTimeline({ groups, atoDate, selectedId, onSelect, sortBy, startHour = 0 }: AtoTimelineProps) {
  const [dayStart, dayEnd] = useMemo(() => {
    // Without an ATO date, frame the earliest takeoff's UTC day.
    let base = atoDate;
    if (!base) {
      const first = groups.flatMap((g) => g.missions).map((m) => parseTime(m.takeoff || m.onStation)).filter((t): t is number => t !== null).sort((a, b) => a - b)[0];
      base = first ? new Date(first).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
    }
    const start = Date.parse(`${base}T00:00:00.000Z`) + startHour * 3600 * 1000;
    return [start, start + 24 * 3600 * 1000];
  }, [atoDate, groups, startHour]);

  const nowPct = useMemo(() => {
    const now = Date.now();
    return now > dayStart && now < dayEnd ? pct(now, dayStart, dayEnd) : null;
  }, [dayStart, dayEnd]);

  const sortedGroups = useMemo(
    () =>
      groups.map((g) => ({
        ...g,
        missions: [...g.missions].sort((a, b) => {
          if (sortBy === "CALLSIGN") return a.callsign.localeCompare(b.callsign);
          const ta = parseTime(a.takeoff || a.onStation) ?? Infinity;
          const tb = parseTime(b.takeoff || b.onStation) ?? Infinity;
          return ta - tb;
        }),
      })),
    [groups, sortBy]
  );

  const hours = Array.from({ length: 25 }, (_, i) => i);

  const renderBar = (m: TimelineMission, color: string) => {
    const takeoff = parseTime(m.takeoff) ?? parseTime(m.onStation);
    const landing = parseTime(m.landing) ?? parseTime(m.offStation);
    const on = parseTime(m.onStation);
    const off = parseTime(m.offStation);
    if (takeoff === null || landing === null || landing <= dayStart || takeoff >= dayEnd) {
      return null;
    }
    const left = pct(takeoff, dayStart, dayEnd);
    const width = Math.max(1.2, pct(landing, dayStart, dayEnd) - left);
    const isSelected = m._id === selectedId;

    const glyphs = [
      m.armed ? "⚡" : "",
      m.locked ? "🔒" : "",
      m.hold ? `H${m.holdType ? `:${m.holdType}` : ""}` : "",
      m.canx ? "CANX" : "",
      m.lastChangeType ? CHANGE_BADGES[m.lastChangeType] || "" : "",
    ].filter(Boolean).join(" ");

    const tooltip = `${m.callsign} · ${m.launchBase || "?"} · ` +
      `${m.onStation ? new Date(m.onStation).toISOString().slice(11, 16) : "--"}Z–` +
      `${m.offStation ? new Date(m.offStation).toISOString().slice(11, 16) : "--"}Z` +
      (glyphs ? ` · ${glyphs}` : "");

    return (
      <Tooltip key={m._id} title={tooltip} placement="top" arrow>
        <Box
          onClick={() => onSelect(m._id)}
          sx={{
            position: "absolute",
            left: `${left}%`,
            width: `${width}%`,
            top: 4,
            height: ROW_HEIGHT - 8,
            borderRadius: "3px",
            backgroundColor: m.canx ? "rgba(255,255,255,0.08)" : `${color}55`,
            border: `1px solid ${isSelected ? "#f72bef" : m.canx ? "#666" : color}`,
            boxShadow: isSelected ? "0 0 6px 1px rgba(247,43,239,0.6)" : undefined,
            cursor: "pointer",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            "&:hover": { borderColor: "#f72bef" },
          }}
        >
          {on !== null && off !== null && off > dayStart && on < dayEnd && (
            <Box
              sx={{
                position: "absolute",
                left: `${((Math.max(on, dayStart) - takeoff) / (landing - takeoff)) * 100}%`,
                width: `${((Math.min(off, dayEnd) - Math.max(on, dayStart)) / (landing - takeoff)) * 100}%`,
                top: 0,
                bottom: 0,
                backgroundColor: m.canx ? "rgba(255,255,255,0.12)" : color,
                opacity: 0.85,
              }}
            />
          )}
          <Typography
            sx={{
              position: "relative",
              px: 0.5,
              fontSize: "0.62rem",
              fontWeight: 700,
              color: "#fff",
              whiteSpace: "nowrap",
              textDecoration: m.canx ? "line-through" : undefined,
              textShadow: "0 0 3px rgba(0,0,0,0.9)",
            }}
          >
            {m.callsign}{glyphs ? ` ${glyphs}` : ""}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  const totalMissions = sortedGroups.reduce((n, g) => n + g.missions.length, 0);

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        backgroundColor: "#1a1c26",
        border: "1px solid #333",
        borderRadius: 1,
        position: "relative",
      }}
    >
      {/* Hour axis */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          height: AXIS_HEIGHT,
          ml: `${LABEL_WIDTH}px`,
          backgroundColor: "#12131b",
          borderBottom: "1px solid #333",
        }}
      >
        {hours.map((h) => (
          <Typography
            key={h}
            sx={{
              position: "absolute",
              left: `${(h / 24) * 100}%`,
              transform: "translateX(-50%)",
              top: 4,
              fontSize: "0.6rem",
              color: "#8a90a0",
            }}
          >
            {String((h + startHour) % 24).padStart(2, "0")}
          </Typography>
        ))}
      </Box>

      {/* Grid + rows */}
      <Box sx={{ position: "relative", ml: `${LABEL_WIDTH}px` }}>
        {/* vertical hour gridlines */}
        {hours.slice(1, 24).map((h) => (
          <Box
            key={h}
            sx={{
              position: "absolute",
              left: `${(h / 24) * 100}%`,
              top: 0,
              bottom: 0,
              width: "1px",
              backgroundColor: h % 6 === 0 ? "#2c2e3a" : "#20222c",
            }}
          />
        ))}
        {/* now line */}
        {nowPct !== null && (
          <Box
            sx={{
              position: "absolute",
              left: `${nowPct}%`,
              top: 0,
              bottom: 0,
              width: "1px",
              backgroundColor: "#ffea00",
              zIndex: 1,
              boxShadow: "0 0 4px #ffea00",
            }}
          />
        )}

        {totalMissions === 0 && (
          <Typography sx={{ p: 2, color: "#8a90a0", fontSize: "0.75rem" }}>
            No missions for these filters.
          </Typography>
        )}

        {sortedGroups.map((g) =>
          g.missions.length === 0 ? null : (
            <Box key={g.name}>
              <Box
                sx={{
                  height: HEADER_HEIGHT,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  backgroundColor: "rgba(255,255,255,0.03)",
                  borderTop: "1px solid #2a2c38",
                  borderBottom: "1px solid #23252f",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: g.color }} />
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#cacce5" }}>
                  {g.name}
                </Typography>
                <Typography sx={{ fontSize: "0.6rem", color: "#8a90a0" }}>
                  {g.missions.length}
                </Typography>
                <Typography sx={{ ml: "auto", fontSize: "0.6rem", color: "#8a90a0" }}>
                  Collection: {collectionTotal(g.missions)}
                </Typography>
              </Box>
              {g.missions.map((m) => (
                <Box key={m._id} sx={{ position: "relative", height: ROW_HEIGHT }}>
                  {renderBar(m, g.color)}
                </Box>
              ))}
            </Box>
          )
        )}
      </Box>
    </Paper>
  );
}
